import React from "react";
import { Alert, AlertTitle, Button } from "@mui/material";

import { Progress } from "ui";
import { StepStatus } from "ui/Progress/Progress";

import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { useAppSelector } from "fond/utils/hooks";

// The workflow has many steps, but we only show 7 in the UI.
const combinedWorkflowSteps = {
  prepare: [0],
  analyze: [1],
  lfn: [2],
  dfn: [3],
  outputs: [4],
  artifacts: [5],
  cts: [6, 7, 8, 9, 10],
};

interface GeneratePanelProps {
  // Flag indicating if the editing is read only
  isReadOnly?: boolean;
}

const GeneratePanel: React.FC<GeneratePanelProps> = ({ isReadOnly }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { workflowState, workflowStepIndex, runWorkflow } = useCustomWorkflow({ versionId, key: "nbn_idd_lite_generate_design" });
  const isGenerating = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  const handleGenerate = async () => {
    runWorkflow?.();
  };

  /**
   * Determines the status of a step based on the workflow step index and state.
   */
  const getStatus = (steps: number[]): StepStatus => {
    if (workflowStepIndex == null) return "Idle";
    if (Math.max(...steps) < workflowStepIndex) return "Success";
    if (steps.includes(workflowStepIndex) && workflowState === "ERROR") return "Error";
    if (steps.includes(workflowStepIndex)) return "InProgress";
    if (workflowState !== "ERROR") return "Idle";

    return "NotReady";
  };

  return (
    <>
      {workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          Design is ready.
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
          Design generated.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 2 }}>
          <AlertTitle>Design generation failed</AlertTitle>
          Design could not be generated.
        </Alert>
      )}

      {(isGenerating || workflowState === "ERROR") && (
        <Progress
          steps={[
            {
              ID: "prepare",
              Label: "Preparing base data",
              Status: getStatus(combinedWorkflowSteps.prepare),
            },
            {
              ID: "analyze",
              Label: "Analyzing the problem",
              Status: getStatus(combinedWorkflowSteps.analyze),
            },
            {
              ID: "lfn",
              Label: "Designing the LFN",
              Status: getStatus(combinedWorkflowSteps.lfn),
            },
            {
              ID: "dfn",
              Label: "Designing the DFN",
              Status: getStatus(combinedWorkflowSteps.dfn),
            },
            {
              ID: "outputs",
              Label: "Creating design outputs",
              Status: getStatus(combinedWorkflowSteps.outputs),
            },
            {
              ID: "artifacts",
              Label: "Creating design artifacts",
              Status: getStatus(combinedWorkflowSteps.artifacts),
            },
            {
              ID: "cts",
              Label: "Calculating cost to serve",
              Status: getStatus(combinedWorkflowSteps.cts),
            },
          ]}
        />
      )}

      {workflowState !== "COMPLETE" && (
        <Button
          fullWidth
          variant="contained"
          size="small"
          sx={{ mt: 2 }}
          disabled={!runWorkflow || isGenerating || isReadOnly}
          onClick={handleGenerate}
        >
          Generate design
        </Button>
      )}
    </>
  );
};

export default GeneratePanel;
